import z from 'zod';
import { BookingState } from '../booking/booking.enums';
import { ChatMessageSchema } from '../chat';
import { MoneySchema, PaginationParamsSchema, PaginationResponseSchema } from '../common';
import {
  CategorySchema,
  CurrencySchema,
  EventProductionMode,
  EventReplayAccess,
  EventState,
  EventStreamingMode,
  HostLivePreviewSchema,
  VisibilitySchema,
} from '../event';
import { HOST_DATE_RANGE_PRESETS } from './host.enums';

/** Shared `range` / `start` / `end` triple. `range` wins: an explicit
 *  preset always computes its own window, and `start`/`end` are read only
 *  when `range` is `custom` or absent. */
export const HostDateRangeQuerySchema = z.object({
  range: z.enum([...HOST_DATE_RANGE_PRESETS, 'custom'] as const).optional(),
  start: z.iso.datetime().optional(),
  end: z.iso.datetime().optional(),
  /** IANA timezone for `range=today` (e.g. America/Los_Angeles). Default UTC. */
  tz: z.string().trim().min(1).max(64).optional(),
});

export const HostOverviewQuerySchema = HostDateRangeQuerySchema;

/** Free-text search term. Trimmed; empty collapses to `undefined`. */
export const HostSearchTermSchema = z
  .string()
  .trim()
  .max(80)
  .optional()
  .transform((value) => (value && value.length > 0 ? value : undefined));

export const HostEventsTabSchema = z.enum(['UPCOMING', 'LIVE', 'ENDED', 'DRAFTS']);

export const HostSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(120),
  limit: z.coerce.number().int().min(1).max(10).default(10),
});

export const HostEventPatchSchema = z.object({
  title: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  coverImageUrl: z.any().optional(),
  previewClipUrl: z.url().nullable().optional(),
  ticketPrice: z.string().optional(),

  category: CategorySchema.optional(),
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }).optional(),
  durationMinutes: z.number().int().positive().optional(),
  tags: z.array(z.string()).optional(),
  location: z.string().max(255).optional(),

  // Access & Pricing
  accessModel: z.enum(['FREE', 'PAID']).optional(),
  currency: CurrencySchema.optional(),
  maxTickets: z.number().int().positive().nullable().optional(),
  visibility: VisibilitySchema.optional(),
  guestEmails: z.array(z.email()).optional(),
  replayAccess: z.enum(EventReplayAccess).optional(),
  replayWindow: z.number().int().positive().optional(),
  replayPrice: z.number().nonnegative().optional(),
  productionMode: z.enum(['SELF', 'HIRE']).optional(),
  crewIds: z.array(z.uuid()).optional(),
  productionNotes: z.string().max(2000).nullable().optional(),
  backupStreamEnabled: z.boolean().optional(),
});

export const HostEventsListQuerySchema = z.object({
  tab: HostEventsTabSchema,
  ...PaginationParamsSchema.shape,
});

export const HostEventRowResponseSchema = z.object({
  eventId: z.string(),
  title: z.string(),
  date: z.iso.datetime(),
  ticketsSold: z.number(),
  plannedDurationMinutes: z.number().nullable(),
  revenue: z.object({
    currency: z.string(),
    value: z.string(),
  }),
  status: z.enum(['ACTIVE', 'PAUSED', 'LIVE', 'ENDED', 'DRAFT', 'CANCELLED']),
});

export const HostEventsTabResponseSchema = z.object({
  tab: HostEventsTabSchema,
  rows: z.array(HostEventRowResponseSchema),
  ...PaginationResponseSchema.shape,
});

export const HostEventsTabCountsResponseSchema = z.object({
  upcoming: z.number(),
  live: z.number(),
  ended: z.number(),
  drafts: z.number(),
});

export const HostEventWizardStartSchema = z.object({
  title: z.string().min(3).max(120).optional(),
});

export const HostEventsListPagedQuerySchema = z.object({
  tab: HostEventsTabSchema,
  mode: z.enum(EventProductionMode).optional(),
  q: HostSearchTermSchema,
  ...HostDateRangeQuerySchema.shape,
  ...PaginationParamsSchema.shape,
});

export const HostEventsTabCountsQuerySchema = z.object({
  q: HostSearchTermSchema,
  ...HostDateRangeQuerySchema.shape,
});

export const HostEventWizardStartResponseSchema = z.object({
  id: z.uuid(),
  state: z.literal('DRAFT'),
  wizardStep: z.number().int().min(1).max(5).nullable(),
});

export const HostEventWizardStepResponseSchema = z.object({
  id: z.uuid(),
  wizardStep: z.number().int().min(1).max(5).nullable(),
  streamingVerified: z.boolean().optional(),
  verificationToken: z.string().optional(),
  crewIds: z.array(z.uuid()).optional(),
  /** Present on step-3 responses when Assign Role rows exist. */
  crewAssignments: z
    .array(
      z.object({
        id: z.uuid(),
        vendorId: z.uuid(),
        role: z.string(),
        state: z.string(),
        compensation: MoneySchema,
        location: z.string(),
      }),
    )
    .optional(),
});

export const HostEventWizardPublishResponseSchema = z.object({
  id: z.uuid(),
  state: z.literal('SCHEDULED'),
  publishedAt: z.iso.datetime().nullable(),
  bookingIds: z.array(z.uuid()),
});

export const HostEventCancelResponseSchema = z.object({
  id: z.uuid(),
  state: z.literal('CANCELLED'),
  cancelledAt: z.iso.datetime().nullable(),
});

/**
 * Overview KPI tile with a money value + prior-period delta. Amount is
 * a MoneyDto per MONEY_MULTI_CURRENCY.md §4 so the FE formats without
 * threading currency separately. `deltaPct` is null when the prior
 * period had zero (avoids "+∞%").
 */
const MoneyKpiSchema = z.object({
  amount: MoneySchema,
  deltaPct: z.number().nullable(),
});

const UpcomingEventsKpiSchema = z.object({
  count: z.number().int(),
  nextStartsAt: z.string().nullable(),
});

const LiveNowKpiSchema = z.object({
  eventCount: z.number().int(),
  totalViewers: z.number().int(),
});

const TotalViewsKpiSchema = z.object({
  value: z.number().int(),
  deltaPct: z.number().nullable(),
});

const LiveEventEntrySchema = z.object({
  eventId: z.uuid(),
  title: z.string(),
  viewers: z.number().int(),
});

const UpcomingEventRailEntrySchema = z.object({
  eventId: z.uuid(),
  title: z.string(),
  scheduledStart: z.string(),
  canGoLive: z.boolean(),
});

const HostActivityEntrySchema = z.object({
  kind: z.string(),
  subjectType: z.enum(['Event', 'Booking', 'PaymentIntent', 'ReplayPurchase']),
  subjectId: z.string(),
  occurredAt: z.number().int(),
  payload: z.record(z.string(), z.unknown()),
  dedupeId: z.string(),
});

export const HostOverviewResponseSchema = z.object({
  range: z.object({
    start: z.string(),
    end: z.string(),
    preset: z.string().optional(),
  }),
  kpis: z.object({
    totalRevenue: MoneyKpiSchema,
    upcomingEvents: UpcomingEventsKpiSchema,
    liveNow: LiveNowKpiSchema,
    totalViews: TotalViewsKpiSchema,
  }),
  live: z.array(LiveEventEntrySchema),
  upcoming: z.array(UpcomingEventRailEntrySchema),
  activity: z.array(HostActivityEntrySchema),
});

export const HostActivityListPageSchema = z.object({
  items: z.array(HostActivityEntrySchema),
  ...PaginationResponseSchema.shape,
});

export const HostSearchHitSchema = z.object({
  eventId: z.uuid(),
  title: z.string(),
  scheduledStart: z.string(),
  state: z.string(),
});

export const HostSearchResponseSchema = z.object({
  hits: z.array(HostSearchHitSchema),
});

export const HostNotificationsUnreadCountSchema = z.object({
  count: z.number().int(),
});

export const HostEventsTabResponseClassSchema = z.object({
  tab: HostEventsTabSchema,
  items: z.array(HostEventRowResponseSchema),
  ...PaginationResponseSchema.shape,
});

export const HostEventFullResponseSchema = z.object({
  id: z.uuid(),
  hostId: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  previewClipUrl: z.string().nullable(),
  venueLocation: z.string().nullable(),
  scheduledStartLocal: z.string(),
  scheduledEndLocal: z.string(),
  scheduledEnd: z.iso.datetime(),
  scheduledStart: z.iso.datetime(),
  timezone: z.string(),
  expectedAudience: z.number().int().nullable(),
  plannedDurationMinutes: z.number().int().nullable(),
  visibility: z.string(),
  accessModel: z.string(),
  /** MoneyDto per MONEY_MULTI_CURRENCY.md §4; null for FREE events. */
  ticketPrice: MoneySchema.nullable(),
  maxTickets: z.number().int().nullable(),
  allowlistData: z.array(z.string()),
  replayAccess: z.enum(EventReplayAccess),
  /** MoneyDto — inherits the event's currency. Zero for free replays. */
  replayAccessFee: MoneySchema,
  retentionDays: z.number().int(),
  replayAvailableUntil: z.string().nullable(),
  shareableUrl: z.string().nullable(),
  state: z.string(),
  tags: z.array(z.string()),
  isFeatured: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
  liveStartedAt: z.string().nullable(),
  liveEndedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  category: z.string(),
  wizardStep: z.number().int().nullable(),
  productionMode: z.string().nullable(),
  productionNotes: z.string().nullable(),
  streamingMode: z.enum(EventStreamingMode).nullable(),
});

export const HostEventWizardCurrentResponseSchema = HostEventFullResponseSchema.extend({
  crewIds: z.array(z.uuid()),
  /** Direct Hire Assign Role rows (additive; empty when FE uses crewIds-only path). */
  crewAssignments: z
    .array(
      z.object({
        id: z.uuid(),
        vendorId: z.uuid(),
        role: z.string(),
        state: z.string(),
        compensation: MoneySchema,
        location: z.string(),
      }),
    )
    .optional()
    .default([]),
});

export const ReadinessItemIdSchema = z.enum([
  'EVENT_DETAILS',
  'PRICING',
  'CREW_ASSIGNED',
  'STREAM_TESTED',
]);

export const ReadinessItemStateSchema = z.enum(['PASS', 'WARN', 'FAIL']);

const ReadinessCtaSchema = z.enum([
  'OPEN_WIZARD_STEP_1',
  'OPEN_WIZARD_STEP_2',
  'OPEN_WIZARD_STEP_3',
  'OPEN_HIRE_CREW',
  'REPLACE_CREW',
  'RUN_STREAM_TEST',
]);

export const ReadinessBlockReasonSchema = z.enum([
  'CHECKLIST_INCOMPLETE',
  'BEFORE_WINDOW',
  'AFTER_WINDOW',
  'ALREADY_LIVE',
  'NOT_OWNED',
]);

export const StreamingStatusSchema = z.enum([
  'NOT_PROVISIONED',
  'READY_FOR_TEST',
  'LIVE',
  'STREAM_TEST_FAILED',
  'STREAM_TEST_SUCCESSFUL',
]);

export const ProductionModeSchema = z.enum(['SELF', 'HIRE']);

export const ReadinessItemSchema = z.object({
  id: ReadinessItemIdSchema,
  label: z.string(),
  state: ReadinessItemStateSchema,
  detail: z.string().optional(),
  cta: ReadinessCtaSchema.optional(),
});

export const HostEventReadinessBlockSchema = z.object({
  progress: z.number().min(0).max(1),
  items: z.array(ReadinessItemSchema),
  canGoLive: z.boolean(),
  blockReason: ReadinessBlockReasonSchema.optional(),
});

/** `GET /host/events/:id/dashboard` — optional active-crew filters */
export const ListHostEventDashboardQuerySchema = z.object({
  /** Case-insensitive match on vendor display name, role, or service category */
  crewSearch: z.string().trim().min(2).max(100).optional(),
  crewState: z.enum(BookingState).optional(),
});

export const HostEventCrewMemberSchema = z.object({
  bookingId: z.string(),
  vendorProfileId: z.string(),
  displayName: z.string(),
  companyName: z.string(),
  vendorAvatarUrl: z.url().nullable(),
  vendorServiceCategoryLabels: z.array(z.string()),
  vendorEquipmentLabels: z.array(z.string()),
  vendorIsVerified: z.boolean(),
  /** From Booking.serviceDetails.role (not scopeOfWork). */
  role: z.string().nullable(),
  /** From Booking.serviceDetails.responsibilities. */
  responsibilities: z.string().nullable(),
  state: z.enum(BookingState),
  confirmedAt: z.iso.datetime().nullable(),
});

export const HostProductionCrewPhaseSchema = z.enum(['ACTIVE', 'PAST']);

/** `GET /host/production/crew` — host-level crew roster across events */
export const ListHostProductionCrewQuerySchema = z.object({
  phase: HostProductionCrewPhaseSchema.default('ACTIVE'),
  eventId: z.uuid().optional(),
  crewSearch: z.string().trim().min(2).max(100).optional(),
  crewState: z.enum(BookingState).optional(),
  ...PaginationParamsSchema.shape,
});

export const HostProductionCrewItemSchema = HostEventCrewMemberSchema.extend({
  eventId: z.uuid(),
  eventTitle: z.string(),
});

export const HostEventAudienceResponseSchema = z.object({
  eventId: z.uuid(),
  totalViewers: z.number().int().nonnegative(),
  uniqueViewers: z.number().int().nonnegative(),
  peakConcurrent: z.number().int().nonnegative(),
  registeredCount: z.number().int().nonnegative(),
  paidCount: z.number().int().nonnegative(),
  refundedCount: z.number().int().nonnegative(),
  returningViewerCount: z.number().int().nonnegative().nullable(),
  highValueBuyerCount: z.number().int().nonnegative().nullable(),
  grossRevenue: MoneySchema,
  computedAt: z.iso.datetime(),
  byDeviceType: z.record(z.string(), z.number()).nullable(),
});

export const HostEventReplayPerformanceResponseSchema = z.object({
  eventId: z.uuid(),
  totalReplayViewers: z.number().int().nonnegative(),
  completedViews: z.number().int().nonnegative(),
  /** Ratio in [0, 1]; null when no session carries a duration yet. */
  avgWatchProgressPct: z.number().min(0).max(1).nullable(),
  totalWatchTimeSec: z.number().int().nonnegative(),
  activeReplayPurchases: z.number().int().nonnegative(),
  replayRevenue: MoneySchema,
  computedAt: z.iso.datetime(),
  points: z.array(
    z.object({
      date: z.string(),
      viewsCount: z.number().int().nonnegative(),
      revenue: MoneySchema,
    }),
  ),
});

export const HostEventDashboardPostEventBlockSchema = z.object({
  audience: HostEventAudienceResponseSchema.nullable(),
  replayPerformance: HostEventReplayPerformanceResponseSchema.nullable(),
});

export const PagedHostProductionCrewResponseSchema = z.object({
  items: z.array(HostProductionCrewItemSchema),
  ...PaginationResponseSchema.shape,
});

export const HostEventDashboardResponseSchema = z.object({
  serverNow: z.iso.datetime(),

  event: z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    coverImageUrl: z.string().nullable(),
    scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
    scheduledStart: z.iso.datetime({ local: true, offset: false }),
    scheduledEndLocal: z.iso.datetime({ local: true, offset: false }),
    scheduledEnd: z.iso.datetime(),
    timezone: z.string(),
    accessModel: z.string(),
    ticketPrice: MoneySchema.nullable(),
    shareableUrl: z.string().nullable(),
    state: z.string(),
    visibility: z.string(),
    productionMode: ProductionModeSchema.nullable(),
    goLiveEligibleAt: z.iso.datetime().nullable(),
    replayAccess: z.enum(EventReplayAccess),
    replayAccessFee: MoneySchema,
    retentionDays: z.number().int(),
    replayAvailableUntil: z.string().nullable(),
  }),

  ticketing: z.object({
    totalPayingCapacity: z.number().int().nullable(),
    ticketsSold: z.number().int(),
    revenue: MoneySchema,
    liveRevenue: MoneySchema,
    ticketRevenue: MoneySchema,
    replayRevenue: MoneySchema,
    hostShare: MoneySchema,
    platformFee: MoneySchema,
  }),

  audience: z.object({
    registered: z.number().int(),
    watchingNow: z.number().int().nonnegative(),
    byCountry: z.record(z.string(), z.number().int()).nullable(),
    byTrafficSource: z.record(z.string(), z.number().int()).nullable(),
  }),

  readiness: HostEventReadinessBlockSchema,

  production: z.object({
    mode: ProductionModeSchema,
    streamingMode: z.enum(EventStreamingMode).nullable(),
    crew: z.array(HostEventCrewMemberSchema),
    /** Cancelled/declined slots not yet replaced — drives the Replace Crew banner. */
    vacantCrewCount: z.number().int().nonnegative(),
  }),

  streaming: z.object({
    status: StreamingStatusSchema,
    serverUrl: z.string().nullable(),
    streamKey: z.string().nullable(),
    lastStreamTestAt: z.iso.datetime().nullable(),
    testAvailableAt: z.iso.datetime().nullable(),
    lastStreamTestFailedAt: z.iso.datetime().nullable(),
    lastStreamTestFailureReason: z.string().nullable(),
  }),

  share: z.object({
    shareableUrl: z.string(),
    slug: z.string(),
  }),
  postEvent: HostEventDashboardPostEventBlockSchema.nullable(),
});

export const HostEventReadinessResponseSchema = HostEventReadinessBlockSchema;

/**
 * `GET /marketplace/vendor/events/:id` — assigned-event execution
 * context for a booked vendor. Dashboard subset: schedule,
 * go-live, readiness, streaming status. Never discloses streamKey
 * (ingest credentials stay host-only).
 */
export const VendorAssignedEventResponseSchema = z.object({
  serverNow: z.iso.datetime(),
  bookingId: z.uuid(),
  event: z.object({
    id: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    coverImageUrl: z.string().nullable(),
    scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
    scheduledStart: z.iso.datetime(),
    scheduledEndLocal: z.iso.datetime({ local: true, offset: false }),
    scheduledEnd: z.iso.datetime(),
    timezone: z.string(),
    state: z.string(),
    visibility: z.string(),
    productionMode: ProductionModeSchema.nullable(),
    goLiveEligibleAt: z.iso.datetime().nullable(),
  }),
  readiness: HostEventReadinessBlockSchema,
  streaming: z.object({
    status: StreamingStatusSchema,
    serverUrl: z.string().nullable(),
    /** Always null — stream key is never disclosed on this surface. */
    streamKey: z.null(),
    lastStreamTestAt: z.iso.datetime().nullable(),
    testAvailableAt: z.iso.datetime().nullable(),
    lastStreamTestFailedAt: z.iso.datetime().nullable(),
    lastStreamTestFailureReason: z.string().nullable(),
  }),
});

// Share endpoint response
export const HostEventShareResponseSchema = z.object({
  shareableUrl: z.string(),
  slug: z.string(),
  copyText: z.string(),
  channelLinks: z.object({
    twitter: z.string(),
    facebook: z.string(),
    email: z.string(),
    embed: z.string(),
  }),
  qrCodeDataUrl: z.string(),
});

// ─── Host live-console (`GET /host/events/:id/live-console`) ─────────

export const LiveConsoleSliceSchema = z.enum(['PRE_LIVE', 'LIVE', 'ENDED']);

export const EndedReplayStateSchema = z.enum(['PROCESSING', 'READY', 'UNAVAILABLE']);

export const LiveIngestHealthSchema = z.enum(['HEALTHY', 'DEGRADED', 'INGEST_DOWN']);

export const EndedReasonSchema = z.enum([
  'HOST_TERMINATED',
  'HOST_TERMINATED_EARLY',
  'AUTO_AFTER_SILENCE',
  'AUTO_AT_SCHEDULED_END',
  'ADMIN_FORCED',
]);

export const EventStreamAlertKindSchema = z.enum([
  'AUDIO_QUALITY',
  'VIDEO_QUALITY',
  'BITRATE_DROP',
  'INGEST_DISCONNECTED',
  'INGEST_RECONNECTED',
  'RECORDING_FAILED',
  'STREAM_DENIED',
]);

export const EventStreamAlertSeveritySchema = z.enum(['INFO', 'WARN', 'ERROR']);

export const HostLiveConsoleEventSchema = z.object({
  id: z.string(),
  title: z.string(),
  scheduledStart: z.iso.datetime(),
  scheduledEnd: z.iso.datetime(),
  scheduledStartLocal: z.iso.datetime({ local: true, offset: false }),
  scheduledEndLocal: z.iso.datetime({ local: true, offset: false }),
  state: z.enum(EventState),
  currency: z.string(),
  accessModel: z.string(),
});

export const HostLiveConsoleControlsSchema = z.object({
  streamEnabled: z.boolean(),
  canGoLive: z.boolean(),
  canEndLive: z.boolean(),
  requiresReauthToEnd: z.boolean(),
});

export const HostLiveConsoleMetricsSchema = z.object({
  viewers: z.number().int(),
  peakViewers: z.number().int(),
  ticketsSold: z.number().int(),
  revenue: z.object({
    value: z.string(),
    currency: z.string(),
  }),
  joinedTotal: z.number().int(),
});

export const HostLiveConsoleTelemetrySchema = z.object({
  bitrateKbps: z.number(),
  resolution: z.string(),
  latencyMs: z.number().nullable(),
  ingestHealth: LiveIngestHealthSchema,
  lastSampleAt: z.iso.datetime(),
});

export const HostLiveConsoleCrewMemberSchema = z.object({
  bookingId: z.string(),
  vendorProfileId: z.string(),
  displayName: z.string(),
  role: z.string().nullable(),
  online: z.boolean(),
});

export const HostLiveConsoleAlertSchema = z.object({
  id: z.string(),
  kind: EventStreamAlertKindSchema,
  severity: EventStreamAlertSeveritySchema,
  message: z.string(),
  startedAt: z.iso.datetime(),
  acknowledgedAt: z.iso.datetime().nullable(),
});

export const HostLiveConsoleChatSchema = z.object({
  enabled: z.boolean(),
  socketNamespace: z.string(),
  backfill: z.array(ChatMessageSchema),
});

export const HostLiveConsoleEndedSchema = z.object({
  endedAt: z.iso.datetime(),
  endedEarly: z.boolean(),
  endedReason: EndedReasonSchema,
  replayState: EndedReplayStateSchema,
  replayAvailableAt: z.iso.datetime().nullable(),
});

export const HostLiveConsolePayloadSchema = z.object({
  serverNow: z.iso.datetime(),
  slice: LiveConsoleSliceSchema,
  event: HostLiveConsoleEventSchema,
  controls: HostLiveConsoleControlsSchema,
  metrics: HostLiveConsoleMetricsSchema,
  telemetry: HostLiveConsoleTelemetrySchema.nullable(),
  crew: z.array(HostLiveConsoleCrewMemberSchema),
  alerts: z.array(HostLiveConsoleAlertSchema),
  activity: z.array(z.never()),
  chat: HostLiveConsoleChatSchema,
  ended: HostLiveConsoleEndedSchema.nullable(),
  livePreview: HostLivePreviewSchema.nullable(),
});

export const HostEventEngagementTimelineQuerySchema = z.object({
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export const HostEventEngagementTimelineBucketSchema = z.object({
  bucketStart: z.iso.datetime(),
  concurrentViewers: z.number().int().nonnegative(),
  chatMessages: z.number().int().nonnegative().nullable(),
  reactions: z.number().int().nonnegative().nullable(),
  bitrateKbps: z.number().int().nonnegative().nullable(),
  ingestHealth: z.enum(['HEALTHY', 'DEGRADED', 'INGEST_DOWN']).nullable(),
  resolution: z.string().nullable(),
});

export const HostEventEngagementTimelineResponseSchema = z.object({
  eventId: z.uuid(),
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  granularity: z.literal('minute'),
  buckets: z.array(HostEventEngagementTimelineBucketSchema),
});

// ─── /host-console socket wire contracts ──────────────────────────────

/**
 * Live-metrics tick. `metrics` shares the shape of the REST
 * `HostLiveConsoleMetrics` snapshot so a shared FE renderer can accept
 * either source. Emitted on `console.metrics.tick`.
 */
export const MetricsTickPayloadSchema = z.object({
  eventId: z.string(),
  metrics: HostLiveConsoleMetricsSchema,
  emittedAt: z.iso.datetime(),
});

/**
 * Live-telemetry tick. Same shape reuse rule as `MetricsTickPayload`.
 * Emitted on `console.telemetry.tick`.
 */
export const TelemetryTickPayloadSchema = z.object({
  eventId: z.string(),
  telemetry: HostLiveConsoleTelemetrySchema,
  emittedAt: z.iso.datetime(),
});

/**
 * Fired when replay processing finishes and playback is available.
 * Discriminated on `toState = 'REPLAY_READY'` because the same channel
 * carries other lifecycle transitions.
 */
export const ReplayReadyStatePayloadSchema = z.object({
  toState: z.literal('REPLAY_READY'),
  replayState: z.literal('READY'),
  playbackUrl: z.url(),
  occurredAt: z.iso.datetime(),
});

/**
 * Discriminator for `console.audience.activity`. Distinct from
 * `MetricsTick` (aggregate counters) — this is the per-event chip
 * stream that renders as "Alice joined", "Ticket sold to Bob".
 */
export const AudienceActivityKindSchema = z.enum(['VIEWER_JOINED', 'VIEWER_LEFT', 'TICKET_SOLD']);

export const AudienceActivityPayloadSchema = z.object({
  /**
   * Deterministic per-activity id (viewing session id for join/leave,
   * payment reference for ticket sold). Lets the FE dedupe across a
   * reconnect + REST backfill without a server-generated uuid.
   */
  id: z.string(),
  eventId: z.string(),
  kind: AudienceActivityKindSchema,
  userId: z.string(),
  displayName: z.string(),
  colorHex: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  /**
   * Ready-to-render chip copy ("Michael joined"). Composed server-side
   * so web and mobile show identical wording — clients display this
   * verbatim rather than building a string from `kind` + `displayName`.
   */
  message: z.string(),
  occurredAt: z.iso.datetime(),
  /**
   * Live viewer count *after* this activity, when the activity is
   * viewership-driven. `null` for `TICKET_SOLD` — a ticket purchase
   * doesn't imply the buyer is currently watching.
   */
  viewerCount: z.number().int().nonnegative().nullable(),
});

/**
 * ACK for `host_console.subscribe`. Success carries the running set of
 * subscribed events on this socket; failure surfaces a stable
 * machine-readable `code`.
 */
export const HostConsoleSubscribeAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    eventId: z.string(),
    subscribed: z.array(z.string()),
  }),
  z.object({
    ok: z.literal(false),
    eventId: z.string(),
    code: z.enum(['invalid_payload', 'unauthorized', 'not_owned', 'cap_reached', 'internal']),
    message: z.string(),
  }),
]);

/** ACK for `host_console.unsubscribe`. */
export const HostConsoleUnsubscribeAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    eventId: z.string(),
    subscribed: z.array(z.string()),
  }),
  z.object({
    ok: z.literal(false),
    eventId: z.string(),
    code: z.enum(['invalid_payload', 'unauthorized', 'internal']),
    message: z.string(),
  }),
]);

/**
 * ACK for `host_console.subscribe_active`. Bulk-joins the caller's
 * active events (SCHEDULED/READY_FOR_TEST/LIVE/PAUSED). `truncated` is
 * set when the caller has more active events than the socket cap.
 */
export const HostConsoleSubscribeActiveAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    subscribed: z.array(z.string()),
    added: z.array(z.string()),
    truncated: z.boolean(),
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['unauthorized', 'internal']),
    message: z.string(),
  }),
]);

// ─── /host-console event-name unions ──────────────────────────────────

export const HostConsoleEvents = {
  AlertRaised: 'console.alert.raised',
  AlertResolved: 'console.alert.resolved',
  StateChanged: 'console.state.changed',
  CrewPresence: 'console.crew.presence',
  MetricsTick: 'console.metrics.tick',
  TelemetryTick: 'console.telemetry.tick',
  AudienceActivity: 'console.audience.activity',
  ChatMessagePosted: 'console.chat.message.posted',
  ChatMessageDeleted: 'console.chat.message.deleted',
  ChatRoomMuted: 'console.chat.room.muted',
  ChatRoomUnmuted: 'console.chat.room.unmuted',
  PreLiveChecklistTick: 'console.pre_live.checklist.tick',
} as const;

export const HostConsoleClientEvents = {
  Subscribe: 'host_console.subscribe',
  Unsubscribe: 'host_console.unsubscribe',
  SubscribeActive: 'host_console.subscribe_active',
} as const;
